<script lang="ts">
  import { LayoutDashboard, Timer, ListChecks, Users, Ellipsis, ChartPie, Settings, LifeBuoy, LogOut, Search, Plus } from '@lucide/svelte';
  import { router } from '../router.svelte';
  import { t } from '../i18n.svelte';
  import { shell } from './shell.svelte';
  import { session } from '../stores/session.svelte';
  import { timer } from '../stores/timer.svelte';
  import { entryEditor } from '../stores/editor.svelte';
  import Modal from '../ui/Modal.svelte';
  import Logo from './Logo.svelte';
  import TimerMini from './TimerMini.svelte';
  import Avatar from '../ui/Avatar.svelte';
  import ColorDot from '../ui/ColorDot.svelte';
  import IconButton from '../ui/IconButton.svelte';

  const tabs = $derived([
    { href: '/app', label: t('nav.dashboard'), icon: LayoutDashboard, exact: true },
    { href: '/app/timer', label: t('nav.timer'), icon: Timer },
    { href: '/app/registraties', label: t('nav.entries'), icon: ListChecks },
    { href: '/app/klanten', label: t('nav.clients'), icon: Users }
  ]);
  const moreActive = $derived(router.isActive('/app/rapporten') || router.isActive('/app/instellingen') || router.isActive('/app/support'));
</script>

<header class="topbar">
  <a href="/app" class="brand" aria-label="Timepeak"><Logo size={26} wordmark={false} /></a>
  <span class="title truncate">{shell.title}</span>
  <div class="right">
    {#if timer.active}<TimerMini variant="pill" />{/if}
    <IconButton label={t('common.search')} onclick={() => (shell.paletteOpen = true)}><Search /></IconButton>
  </div>
</header>

{#if !router.isActive('/app/timer') && !router.isActive('/app/registraties')}
  <button type="button" class="fab" aria-label={t('nav.new_entry')} onclick={() => entryEditor.open()}><Plus size={22} /></button>
{/if}

<nav class="tabbar" aria-label={t('nav.menu')}>
  {#each tabs as tab (tab.href)}
    {@const active = router.isActive(tab.href, tab.exact)}
    <a href={tab.href} class:active aria-current={active ? 'page' : undefined}>
      <span class="ti">
        <tab.icon size={21} strokeWidth={active ? 2.3 : 1.9} />
        {#if tab.href === '/app/timer' && timer.running}<span class="live"><ColorDot color={timer.onBreak ? 'var(--pause)' : 'var(--success)'} shape="round" /></span>{/if}
      </span>
      <span class="tl">{tab.label}</span>
    </a>
  {/each}
  <button type="button" class:active={moreActive} onclick={() => (shell.mobileMoreOpen = true)}>
    <span class="ti"><Ellipsis size={21} /></span>
    <span class="tl">{t('common.more')}</span>
  </button>
</nav>

<Modal bind:open={shell.mobileMoreOpen} title={t('common.more')}>
  <div class="more">
    <div class="me">
      <Avatar name={session.user?.name || session.user?.email} size={40} />
      <div class="who"><strong class="truncate">{session.user?.name}</strong><span class="truncate">{session.user?.email}</span></div>
    </div>
    <a href="/app/rapporten" onclick={() => (shell.mobileMoreOpen = false)}><span class="ic"><ChartPie size={19} /></span>{t('nav.reports')}</a>
    <a href="/app/instellingen" onclick={() => (shell.mobileMoreOpen = false)}><span class="ic"><Settings size={19} /></span>{t('nav.settings')}</a>
    <a href="/app/support" onclick={() => (shell.mobileMoreOpen = false)}><span class="ic"><LifeBuoy size={19} /></span>{t('nav.support')}</a>
    <button type="button" class="danger" onclick={() => { shell.mobileMoreOpen = false; session.logout(); }}><span class="ic"><LogOut size={19} /></span>{t('nav.logout')}</button>
  </div>
</Modal>

<style>
  .topbar {
    position: sticky; top: 0; z-index: var(--z-nav);
    display: flex; align-items: center; gap: var(--space-3);
    height: calc(56px + env(safe-area-inset-top));
    padding: env(safe-area-inset-top) var(--space-3) 0 var(--space-3);
    background: color-mix(in srgb, var(--bg) 82%, transparent);
    backdrop-filter: saturate(1.6) blur(14px);
    -webkit-backdrop-filter: saturate(1.6) blur(14px);
    border-bottom: 1px solid var(--border);
  }
  .brand { display: inline-flex; }
  .title { flex: 1; font-weight: var(--weight-semibold); font-size: var(--text-md); }
  .right { display: flex; align-items: center; gap: var(--space-1_5); }
  .tabbar {
    position: fixed; left: 0; right: 0; bottom: 0; z-index: var(--z-nav);
    display: grid; grid-template-columns: repeat(5, 1fr);
    height: calc(var(--mobile-nav-h) + env(safe-area-inset-bottom));
    padding-bottom: env(safe-area-inset-bottom);
    background: color-mix(in srgb, var(--bg-elevated) 88%, transparent);
    backdrop-filter: saturate(1.6) blur(16px);
    -webkit-backdrop-filter: saturate(1.6) blur(16px);
    border-top: 1px solid var(--border);
  }
  .tabbar a, .tabbar button {
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--space-0_5);
    border: 0; background: transparent; color: var(--text-3); text-decoration: none;
    font-size: var(--text-2xs); font-weight: var(--weight-medium);
    -webkit-tap-highlight-color: transparent;
  }
  .tabbar .active { color: var(--brand); }
  .tabbar a:focus-visible, .tabbar button:focus-visible { outline: none; box-shadow: var(--ring); }
  .ti { position: relative; display: inline-flex; }
  .live { position: absolute; top: -2px; right: -4px; display: inline-flex; border-radius: var(--radius-full); box-shadow: 0 0 0 2px var(--bg-elevated); }
  .fab {
    position: fixed; right: var(--space-4); z-index: var(--z-floating);
    bottom: calc(var(--mobile-nav-h) + var(--space-4) + env(safe-area-inset-bottom));
    width: 52px; height: 52px; border-radius: var(--radius); border: 0;
    background: var(--brand); color: var(--on-brand);
    display: grid; place-items: center;
    box-shadow: var(--shadow-brand);
  }
  .fab:focus-visible { outline: none; box-shadow: var(--ring); }
  .more { display: flex; flex-direction: column; gap: var(--space-1); }
  .me { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-1) var(--space-1) var(--space-3); margin-bottom: var(--space-1_5); border-bottom: 1px solid var(--border); }
  .who { display: flex; flex-direction: column; min-width: 0; }
  .who span { font-size: var(--text-sm); color: var(--text-2); }
  .more a, .more button {
    display: flex; align-items: center; gap: var(--space-3);
    height: var(--control-h-lg); padding: 0 var(--space-2); border: 0; border-radius: var(--radius-sm);
    background: transparent; color: var(--text); font-size: var(--text-md); text-decoration: none; text-align: left;
  }
  .more a:hover, .more button:hover { background: var(--surface-hover); }
  .ic { display: inline-flex; color: var(--text-2); }
  .more .danger, .more .danger .ic { color: var(--danger); }
</style>
